import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import Spade from "@/components/Spade";
import ClientsPanel from "@/pages/admin/ClientsPanel";
import { toast } from "sonner";
import { LogOut, Save, Plus, Trash2 } from "lucide-react";

const TABS = [
    { key: "site", label: "Site Content", tid: ADMIN.tabSite },
    { key: "cases", label: "Case Studies", tid: ADMIN.tabCases },
    { key: "testimonials", label: "Testimonials", tid: ADMIN.tabTestimonials },
    { key: "team", label: "Team", tid: ADMIN.tabTeam },
    { key: "leads", label: "Leads", tid: ADMIN.tabLeads },
    { key: "clients", label: "Clients", tid: ADMIN.tabClients },
];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("site");

    return (
        <div
            data-testid={ADMIN.dashboardRoot}
            className="min-h-screen bg-[#0a0000] text-[var(--text-primary)]"
        >
            <header className="border-b border-[var(--border-subtle)] bg-[rgba(74,5,5,0.4)] backdrop-blur-lg sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Spade size={22} />
                        <span className="font-display text-lg">
                            Aestherium <span className="text-[var(--accent-gold)]">Admin</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                        <span className="text-[var(--text-secondary)] hidden sm:inline">
                            {user?.email}
                        </span>
                        <button
                            data-testid={ADMIN.logout}
                            onClick={() => {
                                logout();
                                navigate("/admin", { replace: true });
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--border-subtle)] hover:border-[var(--accent-gold)] transition-colors"
                        >
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap gap-1">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            data-testid={t.tid}
                            onClick={() => setTab(t.key)}
                            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors ${
                                tab === t.key
                                    ? "border-[var(--accent-gold)] text-[var(--accent-champagne)]"
                                    : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
                {tab === "site" && <SiteContentPanel />}
                {tab === "cases" && <CaseStudiesPanel />}
                {tab === "testimonials" && <TestimonialsPanel />}
                {tab === "team" && <TeamPanel />}
                {tab === "leads" && <LeadsPanel />}
                {tab === "clients" && <ClientsPanel />}
            </main>
        </div>
    );
}

// ---------- Site Content ----------
function SiteContentPanel() {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/site-content").then((r) => setForm(r.data));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/site-content", form);
            setForm(data);
            toast.success("Site content saved");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (!form) return <Loading />;

    const fields = [
        ["hero_stat_value", "Hero Stat (number)"],
        ["hero_stat_label", "Hero Stat (label)"],
        ["brands_count", "Brands Count"],
        ["address", "Registered Address"],
        ["gst", "GST"],
        ["msme", "MSME/Udyam"],
        ["iec", "IEC"],
        ["whatsapp", "WhatsApp (+91…)"],
        ["instagram", "Instagram handle"],
        ["email", "Email"],
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {fields.map(([k, l]) => (
                <label key={k} className="block">
                    <span className="text-[11px] uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 block mb-2">
                        {l}
                    </span>
                    <input
                        value={form[k] || ""}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        className="w-full bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--accent-gold)]"
                    />
                </label>
            ))}
            <div className="md:col-span-2 pt-4">
                <button
                    data-testid={ADMIN.saveSite}
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold text-sm uppercase tracking-[0.2em] hover:bg-[var(--accent-champagne)] disabled:opacity-70"
                >
                    <Save size={14} /> {saving ? "Saving…" : "Save"}
                </button>
            </div>
        </div>
    );
}

// ---------- Case Studies ----------
function CaseStudiesPanel() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/case-studies");
        setList(data || []);
        setLoading(false);
    };
    useEffect(() => {
        load();
    }, []);

    const addNew = async () => {
        try {
            await api.post("/case-studies", {
                client_type: "New Client",
                industry: "Industry",
                hero_stat: "1M reach in 30 days",
                description: "Short story of the result.",
                image_url: "",
                order: (list[list.length - 1]?.order || list.length) + 1,
            });
            toast.success("Case study added");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl">Case Studies</h2>
                <button
                    data-testid={ADMIN.addCase}
                    onClick={addNew}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold text-xs uppercase tracking-[0.2em]"
                >
                    <Plus size={14} /> Add
                </button>
            </div>
            {loading ? (
                <Loading />
            ) : (
                <div className="space-y-4">
                    {list.map((cs) => (
                        <CaseStudyRow key={cs.id} item={cs} onChange={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

function CaseStudyRow({ item, onChange }) {
    const [form, setForm] = useState(item);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/case-studies/${item.id}`, form);
            toast.success("Saved");
            onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };
    const del = async () => {
        if (!window.confirm("Delete this case study?")) return;
        try {
            await api.delete(`/case-studies/${item.id}`);
            toast.success("Deleted");
            onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="rounded-xl border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.02)] p-5 grid grid-cols-1 md:grid-cols-12 gap-3">
            <input
                className="md:col-span-3 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Client type"
                value={form.client_type}
                onChange={(e) => setForm({ ...form, client_type: e.target.value })}
            />
            <input
                className="md:col-span-2 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Industry"
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
            />
            <input
                className="md:col-span-3 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Hero stat"
                value={form.hero_stat}
                onChange={(e) => setForm({ ...form, hero_stat: e.target.value })}
            />
            <input
                className="md:col-span-4 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <input
                className="md:col-span-8 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Image URL"
                value={form.image_url || ""}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            />
            <input
                type="number"
                className="md:col-span-2 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Order"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
            />
            <div className="md:col-span-2 flex gap-2 justify-end">
                <button
                    onClick={save}
                    disabled={saving}
                    className="px-3 py-2 rounded-lg bg-[var(--accent-gold)] text-[var(--brand-dark)] text-xs font-semibold uppercase"
                >
                    Save
                </button>
                <button
                    onClick={del}
                    className="px-3 py-2 rounded-lg border border-red-500/40 text-red-300 text-xs"
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
}

// ---------- Testimonials ----------
function TestimonialsPanel() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/testimonials");
        setList(data || []);
        setLoading(false);
    };
    useEffect(() => {
        load();
    }, []);
    const addNew = async () => {
        try {
            await api.post("/testimonials", {
                name: "New Client",
                business: "Business",
                industry: "Industry",
                quote: "Great results.",
                avatar_initials: "NC",
                order: (list[list.length - 1]?.order || list.length) + 1,
            });
            load();
            toast.success("Added");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl">Testimonials ({list.length})</h2>
                <button
                    data-testid={ADMIN.addTestimonial}
                    onClick={addNew}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold text-xs uppercase tracking-[0.2em]"
                >
                    <Plus size={14} /> Add
                </button>
            </div>
            {loading ? (
                <Loading />
            ) : (
                <div className="space-y-3">
                    {list.map((t) => (
                        <TestimonialRow key={t.id} item={t} onChange={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

function TestimonialRow({ item, onChange }) {
    const [form, setForm] = useState(item);
    const save = async () => {
        try {
            await api.put(`/testimonials/${item.id}`, form);
            toast.success("Saved");
            onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const del = async () => {
        if (!window.confirm("Delete testimonial?")) return;
        try {
            await api.delete(`/testimonials/${item.id}`);
            onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    return (
        <div className="rounded-xl border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.02)] p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
            <input
                className="md:col-span-2 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
                className="md:col-span-2 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Business"
                value={form.business}
                onChange={(e) => setForm({ ...form, business: e.target.value })}
            />
            <input
                className="md:col-span-2 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Industry"
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
            />
            <input
                className="md:col-span-5 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Quote"
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
            />
            <input
                type="number"
                className="md:col-span-1 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
            />
            <div className="md:col-span-12 flex gap-2 justify-end">
                <button
                    onClick={save}
                    className="px-3 py-1.5 rounded-lg bg-[var(--accent-gold)] text-[var(--brand-dark)] text-xs font-semibold uppercase"
                >
                    Save
                </button>
                <button
                    onClick={del}
                    className="px-3 py-1.5 rounded-lg border border-red-500/40 text-red-300 text-xs"
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
}

// ---------- Team ----------
function TeamPanel() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/team");
        setList(data || []);
        setLoading(false);
    };
    useEffect(() => {
        load();
    }, []);
    const addNew = async () => {
        try {
            await api.post("/team", {
                name: "New Member",
                role: "Role",
                bio: "Bio",
                image_url: "",
                order: (list[list.length - 1]?.order || list.length) + 1,
            });
            load();
            toast.success("Added");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl">Team</h2>
                <button
                    data-testid={ADMIN.addTeam}
                    onClick={addNew}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold text-xs uppercase tracking-[0.2em]"
                >
                    <Plus size={14} /> Add
                </button>
            </div>
            {loading ? (
                <Loading />
            ) : (
                <div className="space-y-3">
                    {list.map((m) => (
                        <TeamRow key={m.id} item={m} onChange={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

function TeamRow({ item, onChange }) {
    const [form, setForm] = useState(item);
    const save = async () => {
        try {
            await api.put(`/team/${item.id}`, form);
            toast.success("Saved");
            onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const del = async () => {
        if (!window.confirm("Delete member?")) return;
        try {
            await api.delete(`/team/${item.id}`);
            onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    return (
        <div className="rounded-xl border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.02)] p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
            <input
                className="md:col-span-2 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
                className="md:col-span-2 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
            />
            <input
                className="md:col-span-4 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Bio"
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
            />
            <input
                className="md:col-span-3 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                placeholder="Image URL"
                value={form.image_url || ""}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            />
            <input
                type="number"
                className="md:col-span-1 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
            />
            <div className="md:col-span-12 flex gap-2 justify-end">
                <button
                    onClick={save}
                    className="px-3 py-1.5 rounded-lg bg-[var(--accent-gold)] text-[var(--brand-dark)] text-xs font-semibold uppercase"
                >
                    Save
                </button>
                <button
                    onClick={del}
                    className="px-3 py-1.5 rounded-lg border border-red-500/40 text-red-300 text-xs"
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
}

// ---------- Leads ----------
function LeadsPanel() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/leads");
            setList(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        }
        setLoading(false);
    };
    useEffect(() => {
        load();
    }, []);

    const del = async (id) => {
        if (!window.confirm("Delete lead?")) return;
        try {
            await api.delete(`/leads/${id}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div>
            <h2 className="font-display text-2xl mb-6">Leads · {list.length}</h2>
            {loading ? (
                <Loading />
            ) : list.length === 0 ? (
                <p className="text-[var(--text-secondary)]">No leads yet.</p>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-[var(--border-subtle)]">
                    <table className="w-full text-sm">
                        <thead className="bg-[rgba(255,255,255,0.03)]">
                            <tr className="text-left uppercase text-[10px] tracking-[0.2em] text-[var(--accent-champagne)]/70">
                                <th className="px-4 py-3">When</th>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Business</th>
                                <th className="px-4 py-3">IG</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Message</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((l) => (
                                <tr
                                    key={l.id}
                                    className="border-t border-[var(--border-subtle)]"
                                >
                                    <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">
                                        {new Date(l.created_at).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3">{l.name}</td>
                                    <td className="px-4 py-3">{l.business}</td>
                                    <td className="px-4 py-3 text-[var(--accent-champagne)]">
                                        {l.ig_handle}
                                    </td>
                                    <td className="px-4 py-3">{l.email}</td>
                                    <td className="px-4 py-3 max-w-[240px] truncate">
                                        {l.message || "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => del(l.id)}
                                            className="p-2 rounded-md border border-red-500/30 text-red-300 hover:bg-red-500/10"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Loading() {
    return (
        <p className="text-[var(--text-secondary)] soft-pulse font-display text-lg">
            Loading…
        </p>
    );
}
